// Single source of truth for cluster topology, fetched from /api/topology
// on mount. Falls back to the static defaults in topology.js until the call
// resolves (or if it fails), so components never render against undefined.

import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import {
  ALL_NODES as DEFAULT_ALL_NODES,
  CLUSTERS as DEFAULT_CLUSTERS,
} from './topology';

function buildNodeToCluster(clusters) {
  const m = {};
  Object.entries(clusters).forEach(([id, c]) => {
    c.members.forEach((n) => {
      m[n] = id;
    });
  });
  return m;
}

const DEFAULT_TOPOLOGY = {
  ALL_NODES: DEFAULT_ALL_NODES,
  NODE_COUNT: DEFAULT_ALL_NODES.length,
  CLUSTERS: DEFAULT_CLUSTERS,
  NODE_TO_CLUSTER: buildNodeToCluster(DEFAULT_CLUSTERS),
  NODES_WITH_40G: new Set(DEFAULT_ALL_NODES),
  // True once the backend response has been applied. Components can render
  // immediately with defaults; this flag is only useful if you want to delay
  // showing a topology-driven view until confirmed.
  ready: false,
};

const TopologyContext = createContext(DEFAULT_TOPOLOGY);

export function TopologyProvider({ children }) {
  const [topology, setTopology] = useState(DEFAULT_TOPOLOGY);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get('/api/topology');
        if (cancelled) return;
        const data = res.data || {};
        const nodes = data.all_nodes || DEFAULT_ALL_NODES;
        const clusters = data.clusters || DEFAULT_CLUSTERS;
        setTopology({
          ALL_NODES: nodes,
          NODE_COUNT: nodes.length,
          CLUSTERS: clusters,
          NODE_TO_CLUSTER: buildNodeToCluster(clusters),
          NODES_WITH_40G: new Set(nodes),
          ready: true,
        });
      } catch {
        // Keep defaults — better to render the dashboard with stale topology
        // than block the whole UI on a single failed call.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <TopologyContext.Provider value={topology}>
      {children}
    </TopologyContext.Provider>
  );
}

export function useTopology() {
  return useContext(TopologyContext);
}
